import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EventCard from "./EventCard";
import EventFilters from "./EventFilters";
import ResultDisplay from "./ResultDisplay";
import useEvents from "./useEvents";
import "./EventsTab.css";
import { useAuth } from "../../../../../context/AuthContext";

/**
 * EventsTab is the main container component for the Events tab
 * It manages loading, filtering and displaying social events
 *
 * @returns {JSX.Element}
 */
const EventsTab = () => {
  // Get auth context
  const { getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();

  // Use our custom hook for events state and functionality
  const {
    socialEvents,
    selectedEvents,
    daysAhead,
    calendarUpdateResults,
    expandedEvents,
    loading,
    errorMessage,
    successMessage,
    loadSocialEvents,
    handleDaysAheadChange,
    handleSelectEvent,
    handleSubmitCalendarEvents,
    toggleEventExpansion,
  } = useEvents({ getAuthHeaders, logout });

  // Load events when component mounts
  useEffect(() => {
    loadSocialEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load events when days ahead changes
  useEffect(() => {
    loadSocialEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead]);

  return (
    <div className="events-tab">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 className="events-title" style={{ margin: 0 }}>
          Manage Social Events
        </h2>
        <button
          type="button"
          onClick={() => navigate("/admin/create-event")}
          style={{
            padding: "8px 14px",
            background: "#4a89dc",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 14,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          + New Event
        </button>
      </div>

      {/* Event Filters */}
      <EventFilters
        daysAhead={daysAhead}
        onDaysAheadChange={handleDaysAheadChange}
        onSubmitToCalendar={handleSubmitCalendarEvents}
        onRefresh={loadSocialEvents}
        selectedCount={Object.keys(selectedEvents).length}
        loading={loading}
      />

      {/* Status Messages */}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      {loading && <div className="loading-spinner">Loading...</div>}

      {/* Events List */}
      {socialEvents.length === 0 && !loading ? (
        <div className="no-data">No upcoming social events found</div>
      ) : (
        <div className="events-list">
          {socialEvents.map((event) => (
            <EventCard
              key={event.socialEventId}
              event={event}
              isSelected={!!selectedEvents[event.socialEventId]}
              isExpanded={!!expandedEvents[event.socialEventId]}
              onSelect={() => handleSelectEvent(event.socialEventId)}
              onToggleExpand={() => toggleEventExpansion(event.socialEventId)}
            />
          ))}
        </div>
      )}

      {/* Calendar Update Results */}
      <ResultDisplay results={calendarUpdateResults} />
    </div>
  );
};

export default EventsTab;
